'use client';

import clsx from 'clsx';
import type { ForwardedRef, ReactNode, RefObject } from 'react';
import {
  Tooltip as RACTooltip,
  OverlayArrow as RACOverlayArrow,
  type TooltipProps as RACTooltipProps,
  TooltipTrigger as RACTooltipTrigger,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import type { CommonProps } from '../types.js';
import { Text } from '../typography/text.js';

export interface TooltipProps
  extends Omit<CommonProps, 'id'>, Pick<RACTooltipProps, 'placement' | 'isOpen' | 'onOpenChange'> {
  /** The element that this tooltip describes. Must be focusable and hoverable. */
  children: ReactNode;
  /** The contents of the tooltip. */
  label: string;
  /** The ref for the element which the tooltip positions itself with respect to. */
  triggerRef?: RefObject<Element | null>;
  /** Whether the tooltip is disabled. */
  isDisabled?: boolean;
  /**
   * Whether the tooltip should close when the trigger is pressed.
   * @default true
   * @internal
   */
  shouldCloseOnPress?: boolean;
}

function Tooltip(
  {
    children,
    label,
    isOpen,
    onOpenChange,
    UNSAFE_className,
    UNSAFE_style,
    isDisabled,
    shouldCloseOnPress = true,
    ...props
  }: TooltipProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  return (
    <RACTooltipTrigger
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      delay={300}
      closeDelay={300}
      isDisabled={isDisabled}
      shouldCloseOnPress={shouldCloseOnPress}
    >
      {children}
      <RACTooltip
        {...props}
        ref={ref}
        className={clsx('cim-tooltip', UNSAFE_className)}
        style={UNSAFE_style}
        containerPadding={16}
        offset={5} // 1px border + 4px actual offset
        arrowBoundaryOffset={4} // offset by border radius so that the arrow is never positioned over the rounded corner
        data-cim-style-root
      >
        <RACOverlayArrow className="cim-tooltip-arrow">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="10" viewBox="0 0 16 10">
            <path d="M0 0H16L8.78087 9.02391C8.38054 9.52432 7.61946 9.52432 7.21913 9.02391L0 0Z" />
          </svg>
        </RACOverlayArrow>
        <Text as="p" variant="small">
          {label}
        </Text>
      </RACTooltip>
    </RACTooltipTrigger>
  );
}

/**
 * Provides additional information about an element that is hovered or focused.
 *
 * See [tooltip usage guidelines](https://ui.cimpress.io/components/tooltip/).
 */
const _Tooltip = forwardRef(Tooltip, 'Tooltip');

export { _Tooltip as Tooltip };
